/* Pretty Pixie — keep look-alike products apart in collection grids (15 Oct 2026).
   Look-alike = same print/series (Floral Bow Print, Rose Ruffle, Crimson Paisley, Jacquard...)
   or the same style sold in several colours ("X — Pink", "X — White").
   At least 4 other products sit between two look-alikes (more on wide desktop rows).
   The collection's own sort (best selling, newest, manual) is kept as closely as possible:
   the earliest card that fits is always placed next. Store data is not changed. */
(() => {
  if (window.__ppGridSpread) return;
  window.__ppGridSpread = true;

  const SERIES = [
    'floral bow print', 'crimson paisley', 'sunset crush', 'cutout maxi shirt',
    'eyelet tiered ruffle corset', 'lace-trim pleated v-neck tiered', 'cotton tiered ruffle eyelet',
    'eyelet ruffle', 'pleated square-neck', 'lace halter', 'lace-trim spaghetti strap',
    'halter bustier cotton', 'spaghetti strap bustier', 'bustier spaghetti strap', 'lace-trim bustier strap',
    'pastel floral halter', 'navy paisley', 'jacquard', 'tie-neck cap-sleeve', 'lace-trim off-shoulder',
    'puff-sleeve floral', 'corset tiered', 'corset maxi', 'corset mini', 'bustier', 'toile',
    'peach blossom', 'twilight rose', 'garden rose', 'bandana', 'smocked waist ruffle', 'belted shirt'
  ];
  const COLORS = new Set(('ivory white black pink blush lilac lavender mint green sage olive blue navy indigo ' +
    'royal powder sky light dark teal turquoise aqua coral orange red rust crimson rose peach yellow ' +
    'mustard buttercream lemon beige tan taupe cream plum purple fuchsia magenta damson papaya punch ' +
    'winterberry vintage primrose eclipse off pastel emerald orchid raspberry marigold sunshine ' +
    'midnight noir vibrant brown gold').split(' '));

  const family = (title) => {
    const t = (title || '').toLowerCase().replace(/&/g, ' ');
    if (t.includes('rose') && t.includes('ruffle')) return 'rose ruffle';
    for (const s of SERIES) if (t.includes(s)) return s;
    if (t.includes('—')) return t.split('—')[0].trim();
    return t.split(/[^a-z-]+/).filter((w) => w && !COLORS.has(w)).join(' ');
  };

  const titleOf = (li) =>
    li.querySelector('[data-product-title]')?.getAttribute('data-product-title') ||
    li.querySelector('a[href*="/products/"][aria-label]')?.getAttribute('aria-label') ||
    li.querySelector('[class*="product-title"]')?.textContent?.trim() || '';

  const columns = (grid) => {
    const cols = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
    return Math.max(2, cols || 2);
  };

  // gap = how many cards must sit between two look-alikes (at least 4, more on wide desktop rows).
  // The earliest card that keeps the gap is placed next, so the collection's ranking is kept.
  // When nothing on this page keeps the gap, the leftovers are held back and shown with the next
  // page (infinite scroll), where there is variety to separate them. On the last page everything
  // is shown: a family that would run out of room goes first, and if nothing fits, the card whose
  // look-alike is furthest back is used.
  const spread = (items, prev, gap, last) => {
    const rem = items.slice();
    const out = [];
    const placed = prev.slice();
    while (rem.length) {
      const recent = new Set(placed.slice(-gap).map((x) => x.f));
      let pick;
      if (last) {
        const counts = {};
        rem.forEach((x) => { counts[x.f] = (counts[x.f] || 0) + 1; });
        const n = rem.length;
        const critical = Object.keys(counts).filter((k) => (counts[k] - 1) * (gap + 1) + 1 >= n && !recent.has(k));
        pick = rem.find((x) => !recent.has(x.f) && (!critical.length || critical.includes(x.f)));
      }
      if (!pick) pick = rem.find((x) => !recent.has(x.f));
      if (!pick) {
        if (!last) break; // hold the rest for the next page
        const lastAt = (f) => { for (let i = placed.length - 1; i >= 0; i--) if (placed[i].f === f) return i; return -1; };
        pick = rem.reduce((a, b) => (lastAt(b.f) < lastAt(a.f) ? b : a));
      }
      out.push(pick);
      placed.push(pick);
      rem.splice(rem.indexOf(pick), 1);
    }
    return { order: out, held: rem };
  };

  const show = (li) => { li.style.removeProperty('display'); delete li.dataset.ppHeld; };
  const hide = (li) => { li.style.setProperty('display', 'none', 'important'); li.dataset.ppHeld = '1'; };

  let busy = false;
  const apply = (grid) => {
    if (busy) return;
    const st = grid.__ppState || (grid.__ppState = { placed: [], held: [] });
    // A filter or sort re-render replaces the cards: forget cards that are gone
    st.placed = st.placed.filter((x) => x.li.isConnected && x.li.parentNode === grid);
    st.held = st.held.filter((x) => x.li.isConnected && x.li.parentNode === grid);
    const cards = Array.from(grid.children).filter((el) => el.matches('li.product-grid__item'));
    const fresh = cards.filter((li) => li.dataset.ppSpread !== '1');
    if (!fresh.length) return;
    busy = true;
    try {
      const gap = Math.max(4, columns(grid) + 1);
      const lastPage = Number(grid.dataset.lastPage || 0);
      const pages = [];
      fresh.forEach((li) => {
        const p = li.dataset.page || '1';
        let g = pages.find((x) => x.p === p);
        if (!g) pages.push((g = { p, items: [] }));
        // A card whose name can't be read gets its own family, so it never counts as a look-alike
        g.items.push({ li, f: family(titleOf(li)) || 'id:' + (li.dataset.productId || Math.random()) });
      });
      pages.forEach((g) => {
        const lis = g.items.map((x) => x.li);
        const firstIdx = cards.indexOf(lis[0]);
        const placedIdx = st.placed.map((x) => cards.indexOf(x.li)).filter((i) => i >= 0);
        // A previous page added above (shopper opened ?page=3 and scrolled up): place it as it is
        if (placedIdx.length && firstIdx < Math.min(...placedIdx)) {
          const { order } = spread(g.items, [], gap, true);
          const anchor = lis[lis.length - 1].nextSibling;
          order.forEach((x) => { grid.insertBefore(x.li, anchor); x.li.dataset.ppSpread = '1'; });
          st.placed = order.concat(st.placed);
          return;
        }
        const isLast = !lastPage || Number(g.p) >= lastPage;
        const pool = st.held.concat(g.items);
        const { order, held } = spread(pool, st.placed, gap, isLast);
        const anchor = lis[lis.length - 1].nextSibling;
        order.forEach((x) => { grid.insertBefore(x.li, anchor); x.li.dataset.ppSpread = '1'; show(x.li); });
        // Held cards wait (hidden) at the end, tagged with this page so infinite scroll asks for the next one
        held.forEach((x) => { grid.insertBefore(x.li, anchor); x.li.dataset.ppSpread = '1'; x.li.dataset.page = g.p; hide(x.li); });
        st.placed = st.placed.concat(order);
        st.held = held;
      });
    } finally {
      busy = false;
    }
  };

  const watch = (grid) => {
    if (grid.__ppWatched) return;
    grid.__ppWatched = true;
    apply(grid);
    new MutationObserver(() => requestAnimationFrame(() => apply(grid))).observe(grid, { childList: true });
  };

  const init = () => document.querySelectorAll('ul.product-grid').forEach(watch);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  // Filters / sort re-render the grid: pick up the new one (throttled to once per frame)
  let pending = false;
  new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => { pending = false; init(); });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
